import { QueryParameterBag } from '@smithy/types'

import {
  EntityAlreadyExistsError,
  HttpResponseError,
} from './error-handling.js'

export const toParameterString = (queryParams: QueryParameterBag): string => {
  const parts: string[] = []

  for (const [key, value] of Object.entries(queryParams)) {
    if (value === null) {
      parts.push(key)
    } else if (Array.isArray(value)) {
      for (const item of value) {
        parts.push(`${key}=${encodeURIComponent(item)}`)
      }
    } else {
      parts.push(`${key}=${encodeURIComponent(value)}`)
    }
  }

  return parts.join('&')
}

export enum HttpStatus {
  OK_200 = 200,
  CREATED_201 = 201,
  ACCEPTED_202 = 202,
  NO_CONTENT_204 = 204,
  FOUND_302 = 302,
  BAD_REQUEST_400 = 400,
  UNAUTHORIZED_401 = 401,
  PAYMENT_REQUIRED_402 = 402,
  FORBIDDEN_403 = 403,
  NOT_FOUND_404 = 404,
  REQUEST_TIMEOUT_408 = 408,
  CONFLICT_409 = 409,
  LENGTH_REQUIRED_411 = 411,
  PAYLOAD_TOO_LARGE_413 = 413,
  TOO_MANY_REQUESTS_429 = 429,
  INTERNAL_SERVER_ERROR_500 = 500,
  BAD_GATEWAY_502 = 502,
  SERVICE_UNAVAILABLE_503 = 503,
  GATEWAY_TIMEOUT_504 = 504,
}

const RetryStatusCodes = [
  HttpStatus.TOO_MANY_REQUESTS_429,
  HttpStatus.INTERNAL_SERVER_ERROR_500,
  HttpStatus.BAD_GATEWAY_502,
  HttpStatus.SERVICE_UNAVAILABLE_503,
  HttpStatus.GATEWAY_TIMEOUT_504,
]

export type RetryOptions = {
  maxRetries?: number
  initialDelay?: number
  fetchFn?: (input: Request) => Promise<Response>
}

export enum StandardHttpHeaders {
  AUTHORIZATION = 'authorization',
}

/**
 * Headers we have defined for our own use.
 */
export enum DroidMapHttpHeaders {
  X_DROIDMAP_INTERNAL = 'x-droidmap-internal',
  X_DROIDMAP_CALLING_USER_ID = 'x-droidmap-calling-user-id',
  X_DROIDMAP_ORG = 'x-droidmap-org',
  X_DROIDMAP_PROJECT = 'x-droidmap-project',
  /**
   * The staff member behind a request, when one is (droidmap#2651): set by a
   * service forwarding a staff actor's request to another. Honoured from
   * internal callers only; for everyone else it comes from the token.
   */
  X_DROIDMAP_ACTING_STAFF_ID = 'x-droidmap-acting-staff-id',
  /**
   * The kind of support session behind a forwarded request (`observe` or
   * `assist`), beside {@link X_DROIDMAP_ACTING_STAFF_ID}, so the service it
   * reaches applies the session's rules too: an observe session's call on
   * to another service is still read-only. Honoured from internal callers
   * only, and only with a staff id.
   */
  X_DROIDMAP_ACTING_STAFF_KIND = 'x-droidmap-acting-staff-kind',
  /**
   * Set on a retryable response that the sender has **already** retried, to
   * stop the caller retrying it a second time.
   *
   * The shared Hono error handler sets it on the 502 it answers when a
   * *downstream* service call failed. That 502 is honest — our dependency
   * failed, not the caller's request — but it is on {@link RetryStatusCodes},
   * and the sender's own `retryFetch` has already spent its five attempts
   * against that dependency. Without this header the caller would retry the
   * whole outer request five more times, so one user click becomes 25 calls
   * against a service that is already failing, and every non-idempotent route
   * in the estate becomes newly retryable half-way through its side effects.
   *
   * Deliberately narrow: it suppresses retries only for a response that says
   * so. A 502 from an ALB during a rolling deploy carries no such header and
   * is retried exactly as before — which is the whole reason this is a header
   * rather than dropping 502 from the retry set.
   */
  X_DROIDMAP_NO_RETRY = 'x-droidmap-no-retry',
}

/**
 * AWS HTTP Headers
 *
 * Used to propagate distributed tracing information between services.
 * This is the standard used by AWS X-Ray.
 */
export enum AwsHttpHeaders {
  X_AMZN_TRACE_ID = 'x-amzn-trace-id',
}

/**
 * W3C Trace Context HTTP Headers
 *
 * Used to propagate distributed tracing information between services.
 * This is the standard used by OpenTelemetry.
 *
 * @see https://www.w3.org/TR/trace-context/
 */
export enum W3CTraceContextHttpHeaders {
  TRACE_PARENT = 'traceparent',
  TRACE_STATE = 'tracestate',
}

/**
 * How much of a failed response's body to keep as error `context`. Enough to
 * diagnose from, far short of the 64 KiB at which a structured log value
 * breaks a Grafana Cloud push.
 */
const MAX_ERROR_BODY_CONTEXT_CHARS = 2000

/**
 * Pull the `error` field — and the optional `reason` beside it — out of a
 * DroidMap service's JSON error body.
 *
 * Every service answers a refusal as `{ "error": "<message>" }` — that is the
 * shared Hono error handler's single shape for 400/402/403/404/409 alike — so
 * this is the one place that knows how to read it. A 402 (and the frozen-org
 * 403) also carries a machine-readable `reason` (`bundle_exhausted`,
 * `spend_limit_reached`, …) naming the billing remedy.
 *
 * Answers `undefined` fields rather than throwing for anything that isn't that
 * shape: an empty body, an ALB/CloudFront HTML page, a JSON body with no
 * `error`, or an `error` that isn't a non-blank string. The no-`error` case is
 * deliberate on the wire, not just defensive: the shared Hono handler answers
 * a failed *downstream* call with a bodyless 400 precisely so a client falls
 * back to its own copy instead of showing a transport string. The caller is on a
 * failure path already; losing the message there must never cost it the typed
 * error it was constructing.
 */
const serverFieldsFromBody = (
  body: string,
): { serverMessage?: string; serverReason?: string } => {
  const nonBlank = (value: unknown): string | undefined =>
    typeof value === 'string' && value.trim().length > 0 ? value : undefined
  try {
    const parsed: unknown = JSON.parse(body)
    if (parsed !== null && typeof parsed === 'object') {
      const { error, reason } = parsed as { error?: unknown; reason?: unknown }
      return { serverMessage: nonBlank(error), serverReason: nonBlank(reason) }
    }
  } catch {
    /* not JSON — no server message to recover */
  }
  return {}
}

/**
 * The server's refusal, in its own words, when it is worth showing a user —
 * otherwise `undefined`, and the caller should render its own copy.
 *
 * Two statuses qualify, and only two:
 *
 *  - **400** — a domain refusal (`ValidationError` and friends). The message
 *    is written for the person who hit it: *"No payment method on file — add
 *    a card before upgrading to Pro"*, *"An earlier Pro upgrade charge is
 *    still outstanding — contact support before retrying"*.
 *  - **409** — an already-exists refusal, which arrives as a typed
 *    {@link EntityAlreadyExistsError} rather than an {@link HttpResponseError}
 *    and so carries no status to test.
 *
 * Everything else is deliberately excluded. A 5xx body is the literal
 * `'Internal server error'` and a 404's is `'Not found'`; retries exhausted
 * have no body at all; and a 401 is about the session, not the request. Those
 * are the cases where "please try again in a moment" is the *true* answer, so
 * they fall through to the caller's fallback (droidmap#2536).
 *
 * Positively gated for the same reason {@link isHttpStatusError} is: anything
 * unrecognised — a `TypeError: Failed to fetch`, a rejected token refresh —
 * answers `undefined` and lands on the generic copy. Getting that polarity
 * backwards is how an internal transport string reaches a customer.
 *
 * @example
 * const message =
 *   serverRefusalMessage(mutation.error) ??
 *   'Couldn’t start the upgrade. Please try again in a moment.'
 */
export const serverRefusalMessage = (error: unknown): string | undefined => {
  if (error instanceof EntityAlreadyExistsError) return error.serverMessage
  if (
    error instanceof HttpResponseError &&
    error.status === HttpStatus.BAD_REQUEST_400
  ) {
    return error.serverMessage
  }
  return undefined
}

/**
 * It looks like the official implementation of fetch in the NodeJs runtime
 * has recently added a retry option, but it's not yet available in the
 * version of NodeJs we're using.
 *
 * https://github.com/nodejs/undici/pull/2281
 *
 * We may want to move over to that version when available.
 *
 * @param createRequest
 * @param maxRetries
 * @param initialDelay
 * @returns
 */
export const retryFetch = async (
  createRequest: () => Request,
  { maxRetries = 5, initialDelay = 200, fetchFn = fetch }: RetryOptions = {},
): Promise<Response> => {
  let delay = initialDelay
  // random delay multiplier between 1.8 and 2.3
  const delayMultiplier = 1.8 + Math.random() * 0.5
  // The status of the last response we saw, so the retries-exhausted throw
  // below can still say *what* kept failing. Without it that throw — the 5xx
  // path, the one most worth distinguishing from a 404 — would carry no
  // status at all.
  let lastStatus: number | undefined

  for (let i = 1; i <= maxRetries; i++) {
    const request = createRequest()

    let response: Response
    try {
      response = await fetchFn(request)
    } catch (error) {
      console.error(
        JSON.stringify({
          message: 'Fetch implementation threw an error - no http response',
          attempt: i,
          fetchError: error,
          request: request.url,
          requestBody: request.body,
        }),
      )

      // Our start-odm-stage function will throw a TypeError randomly with
      // the underlying cause being other side socket closed. This only happens
      // with undici fetch in nodejs though. Experimenting with just retrying
      // on this otherwise will have to ditch undici fetch.
      if (
        error instanceof Error &&
        error.name === 'TypeError' &&
        error.message === 'fetch failed'
      ) {
        await new Promise((resolve) => setTimeout(resolve, delay))
        // Increase the delay for the next attempt
        delay *= delayMultiplier
        continue
      } else {
        throw error
      }
    }

    if (response.ok) {
      return response
    }

    lastStatus = response.status

    // Undici fetch requires you to read the response body in order to free up
    // the connection — and a body can only be read once, so this has to happen
    // before the 409 branch below rather than after it. That branch used to do
    // its own `response.json()`, which throws a bare SyntaxError on the
    // non-JSON body an ALB or CloudFront error page returns; going through
    // `serverFieldsFromBody` means a malformed body costs us the message, not
    // the typed error.
    const errorText = await response.text()
    const { serverMessage, serverReason } = serverFieldsFromBody(errorText)

    // Currently we are using the 409 status code to indicate that the entity already exists
    // We can stop retrying and convert the response back to an EntityAlreadyExistsError
    if (response.status === HttpStatus.CONFLICT_409) {
      // The server's own refusal becomes the message. It used to be dropped
      // for the literal 'Entity already exists', which is what every user of
      // every service saw for every 409 — including "You have already used
      // your free Starter trial…", the one sentence that said what to do
      // about it (droidmap#2536). Every other construction site of this class
      // already puts the domain message here, and the shared Hono handler
      // re-emits `err.message` as the 409 body, so keeping it here is also
      // what makes the message survive a service→service→client hop.
      throw new EntityAlreadyExistsError(
        serverMessage ?? 'Entity already exists',
        {
          serverMessage,
          // Truncated because this lands in a structured log field: the shared
          // Hono handler logs `context` on its 409 arm, and a structured log
          // value over 64 KiB makes Grafana Cloud reject the whole push
          // (AGENTS.md → Log Attribute Size). It used to be a re-stringified
          // JSON body, which at least had to parse; now that a non-JSON body
          // gets here too, cap it.
          context: errorText.slice(0, MAX_ERROR_BODY_CONTEXT_CHARS),
        },
      )
    }

    // If the response is not ok, check if we can retry the request — unless
    // the responder has told us it already did (see X_DROIDMAP_NO_RETRY).
    const senderAlreadyRetried =
      response.headers.get(DroidMapHttpHeaders.X_DROIDMAP_NO_RETRY) === 'true'

    if (RetryStatusCodes.includes(response.status) && !senderAlreadyRetried) {
      console.error(
        JSON.stringify({
          message: `Fetch attempt failed`,
          retryAttempt: i,
          route: `${request.method} ${request.url}`,
          body: `${request.body}`,
          retryDelay: delay,
          errorText,
        }),
      )
      await new Promise((resolve) => setTimeout(resolve, delay))
      // Increase the delay for the next attempt
      delay *= delayMultiplier
      continue
    } else {
      throw new HttpResponseError(
        `Failed to call api route - ${response.status} ${response.statusText}`,
        {
          status: response.status,
          serverMessage,
          serverReason,
          context: JSON.stringify({
            responseStatus: response.status,
            responseStatusText: response.statusText,
            route: `${request.method} ${request.url}`,
            body: `${request.body}`,
            attempt: i,
            // Capped for the same reason the 409 branch above caps it — this
            // context reaches structured log fields.
            errorText: errorText.slice(0, MAX_ERROR_BODY_CONTEXT_CHARS),
          }),
        },
      )
    }
  }

  // If we get here, we've exceeded the max retries
  const request = createRequest()
  throw new HttpResponseError(
    `Failed to call api route after the maximum number of retries`,
    {
      status: lastStatus,
      context: JSON.stringify({
        route: `${request.method} ${request.url}`,
        attempts: maxRetries,
        lastStatus,
        request,
      }),
    },
  )
}
